import * as AgenticV2API from "@/services/api/agentic-v2";
import type { FactfindPortalEventKind } from "@/services/api/agentic-v2/types";

/** Lead the Factfind demo actions operate on — stored when an ONB-05 scenario is launched. */
export const FACTFIND_DEMO_LEAD_KEY = "onb05-demo-fa-client-id";
export const FACTFIND_DEMO_LEAD_EVENT = "onb05-demo-fa-client-id";

export function readFactfindDemoLeadId(): string | null {
  if (typeof window === "undefined") return null;
  const stored = window.localStorage.getItem(FACTFIND_DEMO_LEAD_KEY);
  return stored?.trim() ? stored.trim() : null;
}

export function storeFactfindDemoLeadId(leadId: string): void {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(FACTFIND_DEMO_LEAD_KEY, leadId);
  window.dispatchEvent(new CustomEvent(FACTFIND_DEMO_LEAD_EVENT, { detail: leadId }));
}

/** ONB-05 ad-hoc start (enters at `create_factfind`; consent and route still apply). */
export const FACTFIND_START_ACTION = "action:factfind-start";
/** ONB-05-01 admin defaults editor dialog. */
export const FACTFIND_DEFAULTS_ACTION = "action:factfind-defaults";

/** Demo dropdown action → portal event driven through the shared inbound path. */
export const FACTFIND_CLIENT_EVENT_ACTIONS: Record<string, FactfindPortalEventKind> = {
  "action:factfind-client-opens": "opened",
  "action:factfind-client-saves-partial": "saved_partial",
  "action:factfind-client-submits": "submitted_complete",
  "action:factfind-client-submits-gaps": "submitted_with_gaps",
  "action:factfind-client-submits-contradiction": "submitted_with_contradiction",
};

export function isFactfindLeadAction(actionId: string): boolean {
  return actionId === FACTFIND_START_ACTION || actionId in FACTFIND_CLIENT_EVENT_ACTIONS;
}

function requireDemoLeadId(): string {
  const leadId = readFactfindDemoLeadId();
  if (!leadId) {
    throw new Error("Launch an ONB-05 scenario first to pick the Factfind lead.");
  }
  return leadId;
}

/** Run a Factfind demo dropdown action against the stored demo lead. */
export async function runFactfindDemoAction(actionId: string): Promise<{ message: string }> {
  if (actionId === FACTFIND_START_ACTION) {
    const leadId = requireDemoLeadId();
    const result = await AgenticV2API.createFactfind(leadId);
    return {
      message: result.created
        ? `Factfind created for ${result.display_name}`
        : `Factfind already open for ${result.display_name}`,
    };
  }
  const event = FACTFIND_CLIENT_EVENT_ACTIONS[actionId];
  if (event) {
    const leadId = requireDemoLeadId();
    await AgenticV2API.sendFactfindEvent(leadId, event);
    return { message: `Client event sent: ${event}` };
  }
  throw new Error(`Unknown Factfind demo action: ${actionId}`);
}
